import { useState, useEffect, useCallback, useMemo } from 'react';
import { genericErrorAlert } from '../utils/alertUtils';
import { captureException } from '../utils/sentryUtils';
import { useGetLatestApprovalQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';
import { useAppHubUsers } from './useAppHubUsers';
import { useBreezeAgents } from './useBreezeAgents';
import { useAppTeams } from './useAppTeams';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { hasPendingAgentApprovalStep } from '../utils/approvalStepsUtils';
import { ACTOR } from '../constants/actors';
import { useAgentApprovalPolling } from './useAgentApprovalPolling';
import { useCreateApproval } from './useCreateApproval';
import { useApproveApproval } from './useApproveApproval';
import { useRejectApproval } from './useRejectApproval';
import { useCancelApproval } from './useCancelApproval';
import { useUpdateApproval } from './useUpdateApproval';
import { useRequestChangesOnApproval } from './useRequestChangesOnApproval';
import { useReRequestReview } from './useReRequestReview';
export const useApprovalData = (approvalType, approvalAction, approvableObjectId, assetName, approvalObjectIds) => {
  var _networkError;
  const [hasPendingAgentStep, setHasPendingAgentStep] = useState(false);
  const pollInterval = useAgentApprovalPolling({
    hasPendingAgentStep,
    approvableObjectId
  });
  const {
    data: queryData,
    error: queryError,
    loading: queryLoading,
    refetch: queryRefetch
  } = useGetLatestApprovalQuery({
    variables: {
      approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
      approvalAction,
      approvableObjectId: approvableObjectId
    },
    skip: !approvableObjectId,
    /**
     * fetchPolicy 'network-only' ensures fresh data.
     * We cannot remove this as we always need to fetch the latest approval.
     */
    fetchPolicy: 'network-only',
    pollInterval,
    onError: error => {
      genericErrorAlert();
      captureException(error, {
        extra: {
          fingerprint: 'useApprovalData-fetchLatest',
          approvalType,
          approvalAction,
          approvableObjectId
        }
      });
    }
  });
  const fetchError = queryError ? (queryError === null || queryError === void 0 || (_networkError = queryError.networkError) === null || _networkError === void 0 || (_networkError = _networkError.cause) === null || _networkError === void 0 ? void 0 : _networkError.errorType) || 'UNKNOWN_SUBCATEGORY' : undefined;
  const actorUserIds = useMemo(() => {
    const approval = queryData === null || queryData === void 0 ? void 0 : queryData.approval;
    if (!approval) return [];
    const ids = [];
    if (approval.requester.actorType === ACTOR.USER) {
      ids.push(Number(approval.requester.id));
    }
    for (const step of (_approval$approvalSte = approval.approvalSteps) !== null && _approval$approvalSte !== void 0 ? _approval$approvalSte : []) {
      var _approval$approvalSte;
      if (step.approver.actorType === ACTOR.USER) {
        ids.push(Number(step.approver.id));
      }
    }
    return [...new Set(ids)];
  }, [queryData]);
  const actorAgentIds = useMemo(() => {
    const approval = queryData === null || queryData === void 0 ? void 0 : queryData.approval;
    if (!approval) return [];
    const ids = [];
    if (approval.requester.actorType === ACTOR.AGENT) {
      ids.push(approval.requester.id);
    }
    for (const step of (_approval$approvalSte2 = approval.approvalSteps) !== null && _approval$approvalSte2 !== void 0 ? _approval$approvalSte2 : []) {
      var _approval$approvalSte2;
      if (step.approver.actorType === ACTOR.AGENT) {
        ids.push(step.approver.id);
      }
    }
    return [...new Set(ids)];
  }, [queryData]);
  const hasTeamActors = useMemo(() => {
    var _approval$approvalSte3;
    const approval = queryData === null || queryData === void 0 ? void 0 : queryData.approval;
    if (!approval) return false;
    return approval.requester.actorType === ACTOR.TEAM || ((_approval$approvalSte3 = approval.approvalSteps) !== null && _approval$approvalSte3 !== void 0 ? _approval$approvalSte3 : []).some(step => step.approver.actorType === ACTOR.TEAM);
  }, [queryData]);
  const {
    usersRecord: approverUsers,
    loading: approverUsersLoading,
    error: fetchApproverUsersError
  } = useAppHubUsers({
    userIds: actorUserIds
  });
  const {
    agentsRecord: approverAgents,
    loading: approverAgentsLoading
  } = useBreezeAgents({
    agentIds: actorAgentIds
  });
  const {
    teamsRecord: approverTeams,
    loading: approverTeamsLoading
  } = useAppTeams({
    enabled: hasTeamActors
  });
  const resolvedData = useMemo(() => {
    const approval = queryData === null || queryData === void 0 ? void 0 : queryData.approval;
    if (!approval || approverUsersLoading || approverAgentsLoading || hasTeamActors && approverTeamsLoading) return null;
    return toApprovalData(approval, approverUsers, approverAgents, approvalType, approvalAction, approverTeams);
  }, [queryData, approverUsers, approverUsersLoading, approverAgents, approverAgentsLoading, approverTeams, approverTeamsLoading, hasTeamActors, approvalType, approvalAction]);
  useEffect(() => {
    var _resolvedData$approva;
    setHasPendingAgentStep(hasPendingAgentApprovalStep((_resolvedData$approva = resolvedData === null || resolvedData === void 0 ? void 0 : resolvedData.approvalSteps) !== null && _resolvedData$approva !== void 0 ? _resolvedData$approva : []));
  }, [resolvedData]);
  const fetchLatest = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);
  const create = useCreateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    assetName,
    approvalObjectIds,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams
  });
  const approve = useApproveApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const reject = useRejectApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const cancel = useCancelApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const update = useUpdateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const requestChanges = useRequestChangesOnApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const reRequestReview = useReRequestReview({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData
  });
  const api = useMemo(() => ({
    fetchLatest,
    create,
    cancel,
    reject,
    approve,
    update,
    requestChanges,
    reRequestReview
  }), [fetchLatest, create, cancel, reject, approve, update, requestChanges, reRequestReview]);
  return {
    data: resolvedData,
    isLoading: queryLoading || approverUsersLoading || approverAgentsLoading || hasTeamActors && approverTeamsLoading,
    api,
    fetchError,
    hasFetchApproverUsersError: Boolean(fetchApproverUsersError)
  };
};